import React, { useEffect } from "react";
import Layout from "../../../components/Layout";
import { Link, useLocation } from "react-router-dom";
import { FilterContextProvider } from "../../../contexts/FilterContext";
import Chip from "../../../components/Chip";
import DataTable from "../../../components/DataTable";
import useData from "../../../hooks/useData";
import {
  DeleteIcon,
  DownloadIcon,
  EyeOpen,
  PrintIcon,
} from "../../../assets/icons/Icons";
import SearchFilter from "../../../components/filters/SearchFilter";
import ViewBatchSellOrder from "../../../components/batch-order/View";
import DateRangeFilter from "../../../components/filters/DateRangeFilter";
import { formatDate } from "../../../utils/formatDate";
import { useUserContext } from "../../../contexts/UserContext";
import ResetFilters from "../../../components/filters/ResetFilters";
import ExportDataButton from "../../../components/ExportDataButton";
import { useTranslation } from "react-i18next";
import CustomTooltip from "../../../components/CustomTooltip";
import SelectFilter from "../../../components/filters/SelectFilter";
import ConfirmModal from "../../../components/ConfirmModal";
import { DeleteSellOrder } from "../../../api/sell-orders/deleteSellOrder";
import CreateBatchOrder from "../../../components/batch-order/create/Create";
import FilterBatchOrders from "../../../api/batch-order/filterBatchOrders";
import usePrint from "../../../hooks/usePrint";
import { getBatchReceipt } from "../../../api/batch-order/getBatchReceipt";
import { CentsToDollars } from "../../../utils/formatPrice";
import { downloadFile } from "../../../api/downloadFile";
import { ExportSellOrders } from "../../../api/sell-orders/exportSellOrders";
import OfficesFilter from "../../../components/filters/OfficesFilter";
import PaymentTypeFilter from "../../../components/filters/PaymentTypeFilter";
import CompanyFilter from "../../../components/filters/CompanyFilter";
import { useGlobalContext } from "../../../contexts/GlobalContext";
import GiftReceipt from "../../../components/GiftReceipt";

function Index(props) {
  const { t } = useTranslation();

  const {
    data,
    errorMessage,
    loadingData,
    pageCount,
    selectedItem,
    setSelectedItem,
    showViewItem,
    setShowViewItem,
    showCreateItem,
    selectedForDelete,
    setSelectedForDelete,
    setShowCreateItem,
    showDeleteItem,
    setShowDeleteItem,
    deleteItem,
    setdeleteErrorMessage,
    setdeleteLoading,
    deleteErrorMessage,
    deleteLoading,
  } = useData(FilterBatchOrders, DeleteSellOrder);

  const { can: userCan, hasPermission, hasDynamicPermissions } =
    useUserContext();

  const canCreate = hasDynamicPermissions()
    ? hasPermission("sell_order.batch_sell_order", "write")
    : userCan("batch_sell_order", "write");

  const { setPageTitle, setBreadcrumbs } = useGlobalContext();

  useEffect(() => {
    setPageTitle(t("Card Issuing"));
    setBreadcrumbs([t("Card Issuing"), t("Batch Sell Orders")]);

    return () => {
      setPageTitle("");
      setBreadcrumbs([]);
    };
    // eslint-disable-next-line
  }, []);

  const { handlePrint } = usePrint(getBatchReceipt);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const queryParams = {};
  searchParams.forEach((value, key) => {
    queryParams[key] = value;
  });
  const handleExportCSV = async () => {
    try {
      const response = await FilterBatchOrders(-1, queryParams);
      const ids = response.data.map((item) => item.id);
      await downloadFile(
        ExportSellOrders,
        { ids: ids },
        "batch_sell_orders.csv"
      );
    } catch (error) {
      console.error("Failed to download file:", error);
    }
  };

  return (
    <Layout title={t("Batch Sell Orders")} breadcrumbs={t("Card Issuing")}>
      {showCreateItem && (
        <CreateBatchOrder setShowCreateItem={setShowCreateItem} />
      )}

      {showViewItem && (
        <ViewBatchSellOrder
          order={selectedItem}
          onClose={() => setShowViewItem(false)}
        />
      )}

      {showDeleteItem && (
        <ConfirmModal
          title={
            t("Batch Order With ID ") +
            selectedForDelete?.id +
            t(" will be deleted")
          }
          question={t("Please confirm order deletion")}
          onClose={(e) => {
            setShowDeleteItem(false);
            setSelectedForDelete(null);
            setdeleteErrorMessage(null);
            setdeleteLoading(false);
          }}
          onConfirm={deleteItem}
          selectedForDelete={selectedForDelete}
          setSelectedForDelete={setSelectedForDelete}
          isOfficeRequired={true}
          deleteLoading={deleteLoading}
          deleteErrorMessage={deleteErrorMessage}
        />
      )}

      <DataTable
        filtersTop={
          <>
            <FilterContextProvider name="search_in_fields">
              <SelectFilter
                options={[
                  {
                    value: "id",
                    label: t("By ID"),
                  },
                  {
                    value: "user",
                    label: t("By user"),
                  },
                ]}
                defaultValue="id"
                placeholder={<div>{t("Search by")}</div>}
              />
            </FilterContextProvider>
            <FilterContextProvider name="s">
              <SearchFilter placeholderText={t("Search")} />
            </FilterContextProvider>
          </>
        }
        filtersBottom={
          <>
            <FilterContextProvider name="date">
              <DateRangeFilter />
            </FilterContextProvider>
            <FilterContextProvider name="payment_type">
              <PaymentTypeFilter />
            </FilterContextProvider>
            <FilterContextProvider name="office_id">
              <OfficesFilter />
            </FilterContextProvider>
            <FilterContextProvider name="corporate_customer_id">
              <CompanyFilter />
            </FilterContextProvider>
            <FilterContextProvider>
              <ResetFilters />
            </FilterContextProvider>
          </>
        }
        buttons={
          <>
            {canCreate && (
              <button
                className="btn btn-primary btn-medium"
                onClick={(e) => setShowCreateItem(true)}
              >
                {t("Create")}
              </button>
            )}
            <ExportDataButton
              className="btn btn-secondary btn-medium"
              onClick={handleExportCSV}
              label={t("Export CSV")}
              disabled={!data.data?.length}
            />

          </>
        }
        columns={[
          { header: t("Order Number"), accessor: "id" },
          { header: t("Date"), accessor: "created_at" },
          { header: t("Order Type"), accessor: "type" },
          { header: t("Order Status"), accessor: "status" },
          { header: t("User"), accessor: "created_by_name" },
          { header: t("User ID"), accessor: "created_by" },
          { header: t("Total Purchase Amount"), accessor: "total_cost" },
          { header: t("Office"), accessor: "office_name" },
          { header: t("Actions"), accessor: "actions" },
        ]}
        rows={data.data || []}
        pageCount={pageCount}
        loadingData={loadingData}
        error={errorMessage}
        actions={(item) => (
          <div className="d-flex align-items-center">
            {item.status === "completed" && (
              <>
                <Link
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handlePrint({
                      requestParams: { id: item.id },
                      name: `order-${item.id}`,
                      width: "",
                      height: "",
                      download: false,
                    });
                  }}
                >
                  <CustomTooltip
                    icon={<PrintIcon />}
                    message={t("Print Receipt")}
                  />
                </Link>
                <Link
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handlePrint({
                      requestParams: { id: item.id },
                      name: `order-${item.id}`,
                      width: "",
                      height: "",
                      download: true,
                    });
                  }}
                >
                  <CustomTooltip
                    icon={<DownloadIcon />}
                    message={t("Get Receipt")}
                  />
                </Link>
                <GiftReceipt order={item} />
              </>
            )}
            <Link
              to="#"
              onClick={(e) => {
                e.preventDefault();
                setSelectedItem(item);
                setShowViewItem(true);
              }}
            >
              <CustomTooltip icon={<EyeOpen />} message={t("View")} />
            </Link>
            <Link
              to="#"
              onClick={(e) => {
                e.preventDefault();
                setShowDeleteItem(true);
                setSelectedForDelete({ ...selectedForDelete, id: item.id});
              }}
            >
              <CustomTooltip icon={<DeleteIcon />} message={t("Void")} />
            </Link>
          </div>
        )}
        customOutputs={(columnName, value) => {
          switch (columnName) {
            case "status":
              let status = "inactive";
              if (value === "submitted" || value === "completed") {
                status = "active";
              }
              return (
                <Chip
                  state={status}
                  label={status.charAt(0).toUpperCase() + status.slice(1)}
                  showAsTooltop
                />
              );
            case "created_at":
              return formatDate(value);
            case "total_cost":
              return <p className="text-right">{CentsToDollars(value)}</p>;
            default:
              return value;
          }
        }}
      />
    </Layout>
  );
}

export default Index;
